import React, { useCallback, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Bell, CheckCheck, X } from "lucide-react";
import { toast } from "sonner";
import { getNotifications, markNotificationsRead } from "@/lib/api";
import { TYPE_ACCENT, TYPE_GLYPH, TYPE_LABELS } from "@/lib/format";

const timeAgo = (iso) => {
  try {
    const then = new Date(iso).getTime();
    const diff = Math.max(0, Date.now() - then);
    const m = Math.floor(diff / 60000);
    if (m < 1) return "ora";
    if (m < 60) return `${m}m fa`;
    const h = Math.floor(m / 60);
    if (h < 24) return `${h}h fa`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}g fa`;
    return new Date(iso).toLocaleDateString("it-IT", { day: "2-digit", month: "short" });
  } catch {
    return "";
  }
};

export const NotificationsSheet = ({ open, onClose, onRead }) => {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    setData(null);
    try {
      setData(await getNotifications());
    } catch {
      toast.error("Impossibile caricare le notifiche");
    }
  }, []);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      load();
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, load]);

  const hasUnread = !!data?.items?.some((n) => !n.read);

  const handleClose = useCallback(async () => {
    if (hasUnread) {
      try {
        await markNotificationsRead({ all: true });
      } catch (e) {
        console.warn("[Liqo] markNotificationsRead fallito:", e);
      }
      onRead?.();
    }
    onClose();
  }, [hasUnread, onClose, onRead]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && handleClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, handleClose]);

  const markAll = async () => {
    try {
      await markNotificationsRead({ all: true });
      setData((d) => ({ unread_count: 0, items: (d?.items || []).map((n) => ({ ...n, read: true })) }));
      onRead?.();
      toast.success("Tutte segnate come lette");
    } catch {
      toast.error("Operazione non riuscita");
    }
  };

  const items = data?.items || [];

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="notifications-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={handleClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 top-12 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em] text-white/90">
                <Bell size={15} className="text-white/70" />
                NOTIFICHE <span className="text-white/25">//</span> LIQO
              </span>
              <div className="flex items-center gap-2">
                {hasUnread && (
                  <button
                    onClick={markAll}
                    data-testid="notifications-mark-read-button"
                    className="mono flex h-8 items-center gap-1.5 rounded-[6px] border border-white/[0.1] px-2.5 text-[10px] font-medium uppercase tracking-[0.12em] text-white/60 hover:bg-white/[0.05]"
                  >
                    <CheckCheck size={13} />
                    Segna lette
                  </button>
                )}
                <button
                  onClick={handleClose}
                  data-testid="notifications-close-button"
                  aria-label="Chiudi"
                  className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
                >
                  <X size={14} />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-4 py-4">
              {data === null ? (
                <div className="space-y-2" data-testid="notifications-loading">
                  {[0, 1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-16 animate-pulse rounded-[8px] border border-white/[0.06] bg-[#0D0D14]" />
                  ))}
                </div>
              ) : items.length === 0 ? (
                <div className="flex flex-col items-center pt-20 text-center" data-testid="notifications-empty">
                  <Bell size={24} className="text-white/25" />
                  <p className="mono mt-4 text-sm font-light text-white/85">Nessuna notifica</p>
                  <p className="mt-2 max-w-[260px] text-[12px] font-light text-white/40">
                    Quando l'agente genera nuovi insight li troverai qui.
                  </p>
                </div>
              ) : (
                <div className="space-y-2" data-testid="notifications-list">
                  {items.map((n, i) => {
                    const accent = TYPE_ACCENT[n.type] || "rgba(255,255,255,0.6)";
                    const glyph = TYPE_GLYPH[n.type] || "•";
                    const label = TYPE_LABELS[n.type] || n.type;
                    return (
                      <div
                        key={n.id || i}
                        data-testid={`notification-row-${i}`}
                        data-unread={!n.read}
                        className="relative flex items-start gap-3 overflow-hidden rounded-[10px] border bg-[#0D0D14] px-3 py-3"
                        style={{
                          borderColor: n.read ? "rgba(255,255,255,0.06)" : `${accent}40`,
                          background: n.read ? "#0D0D14" : "#101019",
                        }}
                      >
                        {!n.read && <span className="absolute left-0 top-0 h-full w-[2px]" style={{ backgroundColor: accent }} />}
                        <span
                          className="mono mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-[7px] border text-[13px]"
                          style={{ borderColor: `${accent}55`, color: accent, background: `${accent}12` }}
                        >
                          {glyph}
                        </span>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center justify-between gap-2">
                            <span className="caps-tag" style={{ color: accent, opacity: 0.85 }}>
                              {label}
                            </span>
                            <span className="mono shrink-0 text-[10px] tracking-[0.04em] text-white/30">{timeAgo(n.created_at)}</span>
                          </div>
                          <p className="mt-1 truncate text-[13px] font-light text-white/85" data-testid="notification-title">
                            {n.title}
                          </p>
                          {n.key_number && (
                            <p className="mono mt-0.5 text-[12px] font-medium tabular-nums" style={{ color: accent }}>
                              {n.key_number}
                            </p>
                          )}
                        </div>
                        {!n.read && (
                          <span className="mt-1 h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: accent }} aria-label="Non letta" />
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
